import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  Paper
} from '@mui/material';
import { Textarea } from '@/components/textArea';
import {
  DialogPayload,
  DialogSecondaryAction,
  DialogTitleWithCloseAction,
  useDialogPromise
} from '@ianesthesie-kit/redux';
import { TranslatedText } from '@ianesthesie-kit/core';
import { styled } from '@mui/material/styles';
import { AppDialogType, getRiskColor } from '@/config';
import { Emphasis } from '@/modules/surgicalEvent';
import { Property } from 'csstype';

export type TextAreaDialogProps = Omit<Partial<DialogPayload>, 'dialogType'> & {
  initialValue?: string;
  allowEmpty?: boolean;
  titleItem?: {
    text: string;
    emphasis?: Emphasis;
    display?: Property.Display;
  };
};

export const useTextAreaDialog = () => {
  const { waitForDialog } = useDialogPromise();

  return useMemo(
    () => ({
      waitForUserInput: (props: TextAreaDialogProps) =>
        waitForDialog({
          ...props,
          dialogType: AppDialogType.TEXT_AREA
        } as any)
    }),
    [waitForDialog]
  );
};

export const TextAreaDialog = ({
  title,
  message,
  initialValue,
  allowEmpty = true,
  titleItem,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ...props
}: TextAreaDialogProps) => {
  const { closeAndResolve } = useDialogPromise();
  const [value, setValue] = useState(initialValue ?? '');
  const initialValueRef = useRef(initialValue ?? '');
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      textAreaRef.current?.focus();
    }, 10);
    return () => clearTimeout(t);
  }, []);

  const cancel = () => {
    closeAndResolve(undefined);
  };

  const submitDisabled =
    initialValueRef.current === value || (!allowEmpty && !value);
  const isDeleting = !submitDisabled && !value;

  return (
    <Paper
      sx={{
        p: 0,
        position: 'relative',
        // maxWidth: 400,
        minWidth: {
          xs: 'unset',
          md: 400
        },
        minHeight: 200
      }}
    >
      <DialogTitleWithCloseAction onClose={cancel}>
        <span>
          <span style={{ paddingLeft: 2 }}>{title}</span>
          {titleItem && (
            <>
              &nbsp;
              <span style={{ display: titleItem.display ?? 'inline-block' }}>
                <Chip
                  style={{ lineHeight: '1.4rem', maxWidth: '300px' }}
                  size="small"
                  label={titleItem.text}
                  color={getRiskColor(titleItem.emphasis)}
                />
              </span>
            </>
          )}
        </span>
      </DialogTitleWithCloseAction>
      <DialogContent sx={{ position: 'relative', minHeight: 80 }}>
        <DialogContentText>{message}</DialogContentText>
        <NotesArea
          ref={textAreaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <DialogSecondaryAction onClick={cancel}>
          <TranslatedText id="app.cancel" />
        </DialogSecondaryAction>
        <Button
          disabled={submitDisabled}
          onClick={() => closeAndResolve(value)}
          color={isDeleting ? 'error' : 'primary'}
        >
          <TranslatedText id={isDeleting ? 'app.delete' : 'app.ok'} />
        </Button>
      </DialogActions>
    </Paper>
  );
};

const NotesArea = styled(Textarea)`
  max-width: calc(100% - 12px);
  min-width: min(80vw, 400px);
  min-height: 150px;
  max-height: 500px;
  padding: 1rem;
  margin-top: 4px;
`;
